import { useEffect, useState } from 'react';
import { useSocket } from '@/lib/socket';
import { markNetSuiteUpdated, markNetSuiteRefreshing } from './netsuiteFreshness';
import { useLocationContext } from '@/contexts/LocationContext';
import { OperationRow, Stage } from './types';
import { buildWorkOrderConfig, RawWorkOrder } from '@/components/work-orders/configMap';
import { rowMatchesState, selectedStateCode } from '@/lib/locationFilter';

interface RawSalesOrder {
  id: string;
  orderNumber: string;
  date: string;
  customer: string;
  location: string;
  workOrders?: RawWorkOrder[];
}

const STAGES: Stage[] = ['Kitting', 'Fabric', 'Metal', 'Assembly'];

const parseDate = (raw: string) => {
  const [m, d, y] = raw.split('/').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
};

const stageComplete = (wo: RawWorkOrder, stage: Stage): boolean => {
  switch (stage) {
    case 'Kitting': return wo.kittingComplete;
    case 'Fabric': return wo.fabricComplete;
    case 'Metal': return wo.metalComplete;
    case 'Assembly': return wo.finalAssemblyComplete;
  }
};

const predecessorsReady = (wo: RawWorkOrder, stage: Stage): boolean => {
  if (wo.isTKS0026) {
    if (stage === 'Assembly') return wo.metalComplete;
    return true;
  }
  switch (stage) {
    case 'Kitting': return true;
    case 'Fabric': return wo.kittingComplete;
    case 'Metal': return wo.kittingComplete;
    case 'Assembly': return wo.kittingComplete && wo.fabricComplete && wo.metalComplete;
  }
};

const stageAppliesToWorkOrder = (wo: RawWorkOrder, stage: Stage): boolean => {
  if (wo.isTKS0026 && (stage === 'Kitting' || stage === 'Fabric')) return false;
  return true;
};

export function useOperationsData(): OperationRow[] {
  const socket = useSocket();
  const { selectedLocation } = useLocationContext();
  const stateCode = selectedStateCode(selectedLocation);
  const [orders, setOrders] = useState<RawSalesOrder[]>([]);

  useEffect(() => {
    if (!socket) return;
    const handleData = (data: RawSalesOrder[]) => {
      if (Array.isArray(data)) setOrders(data);
      markNetSuiteUpdated();
    };
    socket.on('netsuiteData', handleData);
    socket.on('netsuiteRefreshing', markNetSuiteRefreshing);
    socket.emit('requestNetSuiteData');
    return () => {
      socket.off('netsuiteData', handleData);
      socket.off('netsuiteRefreshing', markNetSuiteRefreshing);
    };
  }, [socket]);

  const rows: OperationRow[] = [];
  orders.forEach((so) => {
    if (!rowMatchesState(so.location, stateCode)) return;
    so.workOrders?.forEach((wo) => {
      const config = buildWorkOrderConfig(wo);
      STAGES.forEach((stage) => {
        if (!stageAppliesToWorkOrder(wo, stage)) return;
        rows.push({
          id: `${wo.id}-${stage}`,
          stage,
          orderNumber: so.orderNumber,
          workOrderId: wo.orderID,
          customer: so.customer,
          fabricType: wo.fabricType || '',
          frameColor: wo.frameColor || '',
          status: stageComplete(wo, stage) ? 'Completed' : 'Open',
          predecessors: predecessorsReady(wo, stage) ? 'Ready' : 'Blocked',
          date: so.date,
          dateTs: parseDate(so.date),
          location: so.location,
          config,
        });
      });
    });
  });
  return rows;
}
